import { effect, inject, Injectable, signal } from '@angular/core';
import { ApiDataAccess, getValue, Query, QueryBuilder } from '@softline/core';
import { DateRange } from '@softline/ui-core';
import { SalesStatisticsGridDataSource } from '../grid/sales-statistics-grid.data-source';
import { applyActiveFilters } from '../helpers/apply-active-filters.function';
import {
  addDateFilter,
  addDbPercentColumn,
  addPeriodColumns,
  addPeriodFilters,
  addValueColumns,
  splitByPeriod,
  STATISTICS_PREFIX,
  valFromRow,
} from '../helpers/document-type-query.helpers';
import { DrillLevel } from '../helpers/drill-levels';
import {
  InventoryValues,
  loadInventoryValues,
  resolveInventoryValues,
  withInventoryValues,
} from '../helpers/inventory-values';
import { ApiRow } from '../types/api-row';
import { ProductDrillRow } from '../types/chart-row';
import { SalesStatisticsQueryService } from './sales-statistics-query.service';

interface LoadResult {
  data: ApiRow[];
}

@Injectable()
export class ProductDrillChartService {
  queryService = inject(SalesStatisticsQueryService);
  private dataAccess = inject(ApiDataAccess);

  private cache = new Map<string, ProductDrillRow[]>();
  /** Lagerwert values per row cache key — same key, separate query. */
  private inventoryCache = new Map<string, Promise<Map<string, InventoryValues>>>();

  readonly productDrillRows = signal<ProductDrillRow[]>([]);
  readonly loading = signal(false);
  readonly hasError = signal(false);
  lastParamsKey = '';

  constructor() {
    let lastInvalidationKey: string | null = null;
    effect(() => {
      const key = JSON.stringify([
        this.queryService.activeFilters(),
        this.queryService.dateRange(),
        this.queryService.referencePeriod(),
        this.queryService.documentType(),
        this.queryService.statisticType(),
        this.queryService.refreshTick(),
      ]);
      if (lastInvalidationKey === null) {
        lastInvalidationKey = key;
        return;
      }
      if (lastInvalidationKey === key) return;
      lastInvalidationKey = key;
      this.cache.clear();
      this.inventoryCache.clear();
      this.lastParamsKey = '';
    });
  }

  async loadRows(
    period: DateRange,
    referencePeriod: DateRange | null,
    level: DrillLevel,
    documentType = 'INVOICE',
    statisticType = 'TOTAL',
    signal?: AbortSignal
  ): Promise<ProductDrillRow[]> {
    // level.code/level.name carry the backend /query2 field names (incl. the
    // article_* record prefixes — backend contract, see drill-levels.ts).
    const cols = { code: level.code, name: level.name };
    // Sub-group codes repeat across main groups, so rows are keyed by the
    // backend id where the level has one — keying by code merged them.
    const keyField = level.id ?? cols.code;
    const cacheKey = JSON.stringify({
      period,
      referencePeriod,
      documentType,
      statisticType,
      level: level.key,
      filters: this.queryService.activeFilters(),
    });
    const ds = new SalesStatisticsGridDataSource(
      this.dataAccess,
      documentType,
      statisticType
    );

    const base = new QueryBuilder<ApiRow>().start(0).end(2000);
    if (level.id) base.column(level.id, { usage: 'group' });
    base.column(cols.code, { usage: 'group' }).column(cols.name, { usage: 'group' });
    applyActiveFilters(base, this.queryService);
    addValueColumns(base);
    addDbPercentColumn(base);
    // Unsorted, the 2000-row window kept an arbitrary subset at the article level.
    base.sort(`${STATISTICS_PREFIX}.net`, 'DESC');

    const cached = this.cache.get(cacheKey);
    if (cached) {
      return this.applyInventory(
        cached,
        ds,
        cols.code,
        cacheKey,
        period,
        referencePeriod
      );
    }

    let currentData: ApiRow[];
    let refData: ApiRow[];

    if (referencePeriod) {
      const builder = base.copy();
      addPeriodColumns(builder);
      addPeriodFilters(builder, period, referencePeriod);
      const result = (await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal
      )) as LoadResult;
      const split = splitByPeriod(result?.data ?? []);
      currentData = split.current;
      refData = split.previous;
    } else {
      const builder = base.copy();
      addDateFilter(builder, period.from, period.to);
      const result = (await ds.loadAbortable(
        { query: builder.build() as Query<object> },
        signal
      )) as LoadResult;
      currentData = result?.data ?? [];
      refData = [];
    }

    // Per-row fallback to the code: a backend without the id column must not
    // collapse every row onto the '' key (one unlabelled bar). Rows with neither
    // are dropped rather than merged.
    const keyOf = (r: ApiRow): string | null => {
      const value = getValue(r, keyField) ?? getValue(r, cols.code);
      return value === undefined || value === null || value === '' ? null : String(value);
    };
    const byKey = (data: ApiRow[]): Map<string, ApiRow> => {
      const map = new Map<string, ApiRow>();
      for (const r of data) {
        const key = keyOf(r);
        if (key !== null) map.set(key, r);
      }
      return map;
    };
    const currentByKey = byKey(currentData);
    const refByKey = byKey(refData);
    const allKeys = [...new Set([...currentByKey.keys(), ...refByKey.keys()])];
    // Rows came back but none carries the key columns: the answer is not in the
    // expected shape — fail visibly instead of drawing an empty chart.
    if (allKeys.length === 0 && currentData.length + refData.length > 0) {
      throw new Error(`statistics response has no ${keyField} on any row`);
    }

    const rows: ProductDrillRow[] = allKeys
      .map((key) => {
        const o = currentByKey.get(key);
        const ref = refByKey.get(key);
        const code = String(getValue(o ?? ref, cols.code) ?? key);
        const name = getValue(o ?? ref, cols.name);
        const pg = name ? `${code} - ${name}` : code;
        const oNet = valFromRow(o, 'net');
        const oWes = valFromRow(o, 'cost');
        const oDb = valFromRow(o, 'contribution_margin');
        const oDbPct = valFromRow(o, 'contribution_margin_percent');
        const refNet = referencePeriod ? valFromRow(ref, 'net') : oNet;
        const refDb = referencePeriod ? valFromRow(ref, 'contribution_margin') : oDb;
        const refDbPct = referencePeriod
          ? valFromRow(ref, 'contribution_margin_percent')
          : oDbPct;
        return {
          code,
          name: name ?? '',
          field: pg,
          minRevenue: Math.min(oNet, refNet),
          dbPos: refNet < oNet ? oNet - refNet : 0,
          dbNeg: refNet > oNet ? refNet - oNet : 0,
          revenue: oNet,
          referenceRevenue: refNet,
          wes: oWes,
          db: oDb,
          dbPercent: oDbPct,
          referenceDb: refDb,
          minDb: Math.min(oDb, refDb),
          dbDeltaPos: refDb < oDb ? oDb - refDb : 0,
          dbDeltaNeg: refDb > oDb ? refDb - oDb : 0,
          referenceDbPercent: refDbPct,
          minDbPercent: Math.min(oDbPct, refDbPct),
          dbPercentDeltaPos: refDbPct < oDbPct ? oDbPct - refDbPct : 0,
          dbPercentDeltaNeg: refDbPct > oDbPct ? refDbPct - oDbPct : 0,
          // Filled in by applyInventory — the Lagerwert aggregate is loaded
          // separately and only when the toggle is on.
          inventoryValue: 0,
          referenceInventoryValue: 0,
          minInventoryValue: 0,
          inventoryDeltaPos: 0,
          inventoryDeltaNeg: 0,
        };
      })
      .sort((a: ProductDrillRow, b: ProductDrillRow) => b.revenue - a.revenue);
    this.cache.set(cacheKey, rows);
    return this.applyInventory(rows, ds, cols.code, cacheKey, period, referencePeriod);
  }

  /** Adds the Lagerwert numbers to already-mapped rows, loading them with one
      narrow query (cached per parameter set) the first time they're needed. */
  private async applyInventory(
    rows: ProductDrillRow[],
    ds: SalesStatisticsGridDataSource,
    keyField: string,
    cacheKey: string,
    period: DateRange,
    referencePeriod: DateRange | null
  ): Promise<ProductDrillRow[]> {
    if (!this.queryService.inventoryEnabled()) return rows;
    const keys = [...new Set(rows.map((row) => String(row.code ?? '')))].filter(Boolean);
    if (keys.length === 0) return rows;
    // Key column only — the name column is a translation record the stock arm
    // cannot group by. Window sized from the keys: with a comparison period the
    // backend returns one row per key and period.
    const inventoryBase = new QueryBuilder<ApiRow>()
      .start(0)
      .end(keys.length * 2 + 10)
      .column(keyField as never, { usage: 'group' });
    applyActiveFilters(inventoryBase, this.queryService);
    const values = await resolveInventoryValues(this.inventoryCache, cacheKey, () =>
      // No caller signal: the promise is shared between loads, so one load's
      // abort must not doom another's Lagerwert values.
      loadInventoryValues(ds, inventoryBase, keyField, period, referencePeriod, { keys })
    );
    if (!values) return rows;
    return rows.map((row) =>
      withInventoryValues(row, values.get(String(row.code ?? '')))
    );
  }
}
